/**
 * The **build-completeness advisor** — a reusable, read-only model that, given
 * `{ pilot, computed, compendium }`, emits an ordered
 * `BuildSignal[]` describing what is still unfinished about a pilot's build: unspent
 * license / mech-skill / skill-trigger / talent ranks, an LL0 build not yet spread across
 * its three distinct talents / four distinct skill triggers, available-but-unpicked core
 * bonuses, open pilot-gear slots, empty mounts, unspent system points, and an
 * over-budget loadout (over-SP).
 *
 * **Mirror, never re-derive (the core invariant).** Every figure here is read from the
 * SAME single-source the reducer wall and the editor kit affordances use — it re-derives
 * NO budget math:
 *   - `licenseBudget` / `spentLicenseRanks` (`@lancer-vtt/domain/licenses`),
 *   - `haseBudget` / `skillBudget` / `talentBudget` / `rankSum` / `LL0_MAX_RANK`
 *     (`@lancer-vtt/domain/budgets` — the same module the reducer's `rankCeilingAtLevel`
 *     wall reads, so the LL0 shape advisory can't drift from the LL0 rank wall),
 *   - `coreBonusAffordance` (the kit affordance over `coreBonusBudget` + `meetsCoreBonusPrereq`),
 *   - `pilotGearAffordance` (the kit affordance over `pilotGearCounts` + `PILOT_GEAR_LIMITS`),
 *   - SP from the host-computed `ComputedMechStats.spMax`/`spUsed` (which `compute.ts`
 *     produced via `computeSpMax`/`computeSpUsed` — reading it re-derives nothing),
 *   - empty mounts from `composedMounts` (so a core-bonus bonus mount counts like a native
 *     one), netted against `superheavyBraceState().required` so a Superheavy's by-design
 *     brace mount isn't advertised as work (both `@lancer-vtt/domain`).
 * If a cap changes in one of those single-sources, this advisor moves with it for free.
 *
 * **Host-agnostic by construction.** It takes only `pilot` / `computed` / `compendium` —
 * no desktop/roster/session state — so any build-review surface can mount the same model.
 * Presentation (the manufacturer accent, the row chrome, the "research ↗" deep-link) lives
 * in the host that renders the signals, not here.
 */
import {
  licenseBudget,
  spentLicenseRanks,
  haseBudget,
  skillBudget,
  talentBudget,
  rankSum,
  LL0_MAX_RANK,
  composedMounts,
  superheavyBraceState,
  loadoutViolations,
  type PilotData,
  type MechData,
} from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import type { Compendium } from "@lancer-vtt/compendium";
import { coreBonusAffordance } from "./coreBonusAffordance";
import { pilotGearAffordance } from "./pilotGearAffordance";

/** The build sections, in checklist order. A host iterates these to render the full list. */
export const BUILD_SECTIONS = [
  "Licenses",
  "Mech Skills",
  "Skill Triggers",
  "Talents",
  "Core Bonuses",
  "Pilot Gear",
  "Loadout",
] as const;

export type BuildSection = (typeof BUILD_SECTIONS)[number];

/** `todo` = room to finish (under budget / open slot); `warn` = an invalid/over state. */
export type BuildSeverity = "todo" | "warn";

/** The part of the MECH sheet a Loadout signal is about: the chassis, the mounts, or the systems and their SP. */
export const LOADOUT_PARTS = ["frame", "weapons", "systems"] as const;

export type LoadoutPart = (typeof LOADOUT_PARTS)[number];

/** One thing unfinished (or wrong) about a build, attributed to a checklist section. */
export type BuildSignal = {
  section: BuildSection;
  severity: BuildSeverity;
  message: string;
  /** Loadout signals only: which part of the mech the signal is about. */
  part?: LoadoutPart;
};

/** The read-only inputs the advisor derives from — the host seam shape FORGE and the sheets pass. */
export type BuildAdvisorInput = {
  pilot: PilotData;
  /** The host-computed pilot (FORGE passes `roster.computed[id]`); `undefined` mid-load. */
  computed: ComputedPilot | undefined;
  compendium: Compendium;
};

/** "1 license rank" / "2 license ranks" — count + auto-pluralized noun. */
function countNoun(n: number, singular: string): string {
  return `${n} ${singular}${n === 1 ? "" : "s"}`;
}

/** The active mech (the one the editor/build is centered on), or `undefined` if none. */
function activeMech(pilot: PilotData): MechData | undefined {
  return pilot.mechs.find((m) => m.id === pilot.activeMechId) ?? pilot.mechs[0];
}

/**
 * An allocation signal for a `used`-vs-`budget` cap: under budget is a `todo` (room to
 * spend), over budget is a `warn` (over-allocated — reachable after a level-down, which
 * shrinks the cap without cascading the allocation away). `noun` is the unit ("license
 * rank", "talent rank", …); at-budget yields no signal.
 */
function allocationSignal(args: {
  section: BuildSection;
  used: number;
  budget: number;
  noun: string;
}): BuildSignal | null {
  const { section, used, budget, noun } = args;
  const remaining = budget - used;
  if (remaining > 0) return { section, severity: "todo", message: `${countNoun(remaining, "unspent " + noun)}` };
  if (remaining < 0) return { section, severity: "warn", message: `over-allocated by ${countNoun(-remaining, noun)}` };
  return null;
}

/**
 * The **LL0 starting shape** — the one completeness property the Σ-rank budgets can't express.
 *
 * RAW p.18: an LL0 pilot's build is *three distinct rank-I talents* and *four distinct +2 skill
 * triggers*. That's a count of **distinct keys**, and a Σ budget is blind to it — 3 talent ranks
 * is satisfied just as well by one rank-III talent. The reducer's half of this is the per-command
 * `rankCeilingAtLevel` wall (rank ≤ {@link LL0_MAX_RANK} at LL0); this is the **completeness**
 * half, and it deliberately is *not* a wall: a pilot mid-build legitimately sits at 1 or 2
 * talents on the way to 3, so "too few distinct keys" is a state to advise on, never to reject.
 *
 * The required count is **derived, not a third hardcoded number**: at LL0 no key may hold more
 * than `LL0_MAX_RANK`, so spending a budget of `B` takes `ceil(B / LL0_MAX_RANK)` distinct keys.
 * Change either single-source and this moves with it, same as every other figure in this file.
 *
 * Severity splits on whether the budget still has room:
 *   - **under budget** → `todo`. The ordinary mid-build state; the allocation signal beside it
 *     counts the ranks, this one names the shape they have to land in.
 *   - **at or over budget, yet too few distinct keys** → `warn`. The ranks are *concentrated*
 *     (a rank-III talent at LL0) — an invalid shape the budget alone reads as finished. The
 *     reducer rejects reaching it, so this can only be a saved pilot that predates the wall:
 *     surfaced, never silently stripped.
 * Note the two can't disagree with the section's warn-before-todo order: at budget the
 * allocation signal is silent, so a `warn` here only ever sits beside the allocation `warn`.
 */
function ll0ShapeSignal(args: {
  section: BuildSection;
  held: Record<string, number>;
  budget: number;
  noun: string;
}): BuildSignal | null {
  const { section, held, budget, noun } = args;
  const required = Math.ceil(budget / LL0_MAX_RANK);
  const distinct = Object.values(held).filter((rank) => rank > 0).length;
  if (distinct >= required) return null;
  return {
    section,
    severity: rankSum(held) >= budget ? "warn" : "todo",
    message: `needs ${countNoun(required, "distinct " + noun)} at LL0 (${distinct} held)`,
  };
}

/**
 * Emit the ordered build-completeness signals for a pilot. Each is attributed to a
 * {@link BuildSection}; sections appear in {@link BUILD_SECTIONS} order, and within a
 * section a `warn` precedes a `todo`. A fully-built pilot yields `[]`.
 */
export function buildSignals({ pilot, computed, compendium }: BuildAdvisorInput): BuildSignal[] {
  const out: BuildSignal[] = [];
  const push = (s: BuildSignal | null) => {
    if (s) out.push(s);
  };

  // ── Licenses — non-GMS ranks vs LL budget ───────────────────────────────────────
  push(allocationSignal({ section: "Licenses", used: spentLicenseRanks(pilot), budget: licenseBudget(pilot), noun: "license rank" }));

  // ── Mech Skills (HASE) — Σ hull/agi/sys/eng vs 2+level budget ─────────────────────
  const haseUsed = pilot.mechSkills.hull + pilot.mechSkills.agi + pilot.mechSkills.sys + pilot.mechSkills.eng;
  push(allocationSignal({ section: "Mech Skills", used: haseUsed, budget: haseBudget(pilot), noun: "mech-skill point" }));

  // ── Skill Triggers — Σ ranks vs 4+level budget, then the LL0 distinct-key shape ──
  // The shape half is gated on the LEVEL, not on the counts: from LL1 up, reallocation
  // (p.18) makes a concentrated high rank legal, so there is no shape to fall short of.
  // Each section's pair stays adjacent so the emitted list keeps its documented
  // `BUILD_SECTIONS` order.
  const isLL0 = pilot.level === 0;
  push(allocationSignal({ section: "Skill Triggers", used: rankSum(pilot.skills), budget: skillBudget(pilot), noun: "skill-trigger rank" }));
  if (isLL0) push(ll0ShapeSignal({ section: "Skill Triggers", held: pilot.skills, budget: skillBudget(pilot), noun: "skill trigger" }));

  // ── Talents — Σ ranks vs 3+level budget, then the LL0 distinct-key shape ─────────
  push(allocationSignal({ section: "Talents", used: rankSum(pilot.talents), budget: talentBudget(pilot), noun: "talent rank" }));
  if (isLL0) push(ll0ShapeSignal({ section: "Talents", held: pilot.talents, budget: talentBudget(pilot), noun: "talent" }));

  // ── Core Bonuses — the kit affordance (budget + per-manufacturer prereq) ──────────
  const cb = coreBonusAffordance(pilot, compendium);
  const cbInvalid = cb.rows.filter((r) => r.status.state === "held-invalid").length;
  if (cbInvalid > 0) {
    push({
      section: "Core Bonuses",
      severity: "warn",
      message:
        cbInvalid === 1
          ? "1 core bonus no longer meets its license prerequisite"
          : `${cbInvalid} core bonuses no longer meet their license prerequisites`,
    });
  }
  const cbOpen = cb.budget - cb.used;
  const cbHasAvailable = cb.rows.some((r) => r.status.state === "available");
  if (cbOpen > 0 && cbHasAvailable) {
    push({
      section: "Core Bonuses",
      severity: "todo",
      message: `${cbOpen} core ${cbOpen === 1 ? "bonus" : "bonuses"} available to pick`,
    });
  }

  // ── Pilot Gear — open slots across the per-category limits (2 weapon / 1 armor / 3 gear) ─
  const pg = pilotGearAffordance(pilot, compendium);
  const gearOpen = pg.categories.reduce((n, c) => n + Math.max(0, c.limit - c.used), 0);
  if (gearOpen > 0) {
    push({ section: "Pilot Gear", severity: "todo", message: `${countNoun(gearOpen, "pilot-gear slot")} open` });
  }

  // ── Loadout — pick a frame (todo), else over-SP (warn) then empty mounts (todo) ──
  // Scoped to the active mech. A frameless mech has no mounts to flag, so "pick a chassis"
  // stands in as the loadout task until a frame is selected (the onboarding starting point).
  const mech = activeMech(pilot);
  if (mech && !mech.frameId) {
    push({ section: "Loadout", part: "frame", severity: "todo", message: "no chassis — pick a frame" });
  } else if (mech) {
    const stats = computed?.mechs.find((m) => m.id === mech.id)?.stats;
    if (stats && stats.spUsed > stats.spMax) {
      push({
        section: "Loadout",
        part: "systems",
        severity: "warn",
        message: `over system-point budget (${stats.spUsed}/${stats.spMax} SP)`,
      });
    } else if (stats && stats.spUsed < stats.spMax) {
      // Unspent SP — system points still to allocate (the new pilot's first mech sits at 0/N).
      // A `todo` like every other budget: spend it, or accept a leaner build and ignore it.
      push({
        section: "Loadout",
        part: "systems",
        severity: "todo",
        message: `${countNoun(stats.spMax - stats.spUsed, "unspent system point")} (${stats.spUsed}/${stats.spMax} SP)`,
      });
    }
    // Composed — not the raw `loadout.mounts` — so a bonus mount (Improved Armament's
    // Flex, Integrated Weapon's Aux) the pilot holds counts toward "empty mounts" too;
    // the loadout's own composed view is what the editor renders.
    const loadout = mech.loadouts[mech.activeLoadoutIndex];
    const mounts = loadout ? composedMounts(loadout, pilot, compendium) : [];
    const emptyMounts = mounts.filter((mount) => mount.slots.every((slot) => !slot.weaponId)).length;
    // A Superheavy's brace mount is empty BY DESIGN, not unfinished work: RAW (core p.32) spends
    // one OTHER mount per mounted Superheavy as bracing, and the reducer's `superheavy` tenant
    // rejects filling it. Counting it would hand the owner of a legal build a task the wall
    // forbids — so the open count nets the braces out. `required` is read from the SAME shared
    // `superheavyBraceState` the tenant and the mech editor's RESERVED · BRACING affordance read
    // (its `freeMounts` is this same `emptyMounts` count); the advisor derives no bracing of its
    // own. Clamped at 0 for a saved loadout that under-braces: that's an invalid state to
    // surface, not an open mount to fill.
    const brace = superheavyBraceState(mech, pilot, compendium);
    const openMounts = Math.max(0, emptyMounts - (brace?.required ?? 0));
    if (openMounts > 0) {
      push({ section: "Loadout", part: "weapons", severity: "todo", message: countNoun(openMounts, "empty mount") });
    }

    // Any OTHER loadout invariant a context-changing command (a core-bonus untake, an
    // un-retrofit, a level-down) can leave persisted-invalid without the command ever
    // being rejected — those are allowed and flagged, not rejected. SP is already
    // surfaced above via the host-computed stats (`stats.spUsed`/`spMax`), so this
    // filters `kind !== "sp"` to avoid a duplicate; Unique/AI/Superheavy/Flex have no
    // other advisor representation. One signal per violation, same granularity as the
    // "empty mount" push above. AI is a systems rule; Unique, Superheavy and Flex point at the mounts.
    for (const violation of loadoutViolations(mech, pilot, compendium)) {
      if (violation.kind === "sp") continue;
      push({ section: "Loadout", part: violation.kind === "ai" ? "systems" : "weapons", severity: "warn", message: violation.message });
    }
  }

  return out;
}
